import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import FunnelModule from "highcharts/modules/funnel";
import { Chart } from "@highcharts/react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Moment from "moment";
import Swal from "sweetalert2";
import { Circles } from "react-loader-spinner";
import { Card, CardHeader, CardBody } from "./../../components/card/card.jsx";
import "bootstrap-daterangepicker/daterangepicker.css";
import "moment/locale/es";

Moment.locale("es");

const loadHighchartsModule = (module) => {
  if (typeof module === "function") {
    module(Highcharts);
    return;
  }

  if (module && typeof module.default === "function") {
    module.default(Highcharts);
  }
};

loadHighchartsModule(FunnelModule);

function DashboardRendimiento() {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    direccion: "Todas",
    razonSocial: "Todas",
    agencia: "Todas",
    departamento: "Todas",
    tipoPago: "Todas",
  });

  const datePickerRef = useRef(null);
  const [datePickerResetKey, setDatePickerResetKey] = useState(0);

  const accessToken =
    "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3";
  const apiUrl = "https://api.checkmark.mx/solicitudes/consultar";

  const isDarkMode = document.body.classList.contains("theme-default");
  const textColor = isDarkMode ? "#e5e7eb" : "#111827";
  const mutedColor = isDarkMode ? "#9ca3af" : "#6b7280";
  const softTextColor = isDarkMode ? "#cbd5e1" : "#4b5563";
  const gridColor = isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const chartBg = "transparent";
  const cardBg = isDarkMode ? "#111827" : "#f8f8f8";

  const colorSet = {
    azul: "#2f80ed",
    azulOscuro: "#1f3c88",
    naranja: "#f2994a",
    rojo: "#eb5757",
    morado: "#9b51e0",
    turquesa: "#27ae60",
    amarillo: "#f2c94c",
    gris: "#bdbdbd",
    rosa: "#e056fd",
    celeste: "#56ccf2",
  };

  const STATUS_ORDER = ["Autorizado", "Rechazado", "Pendiente de Buro"];

  const CONTADO_STATUS_ORDER = ["Cotización", "Formalizado"];

  const SUBSTATUS_ORDER = [
    "Sin substatus",
    "Analisis",
    "Incompleto",
    "Condicionado Viable",
    "Condicionado No Viable",
    "Facturado",
    "Formalizado",
    "Aprobado Sin Formalizar",
    "Rechazado",
  ];

  const PLANES_ORDER = ["Elite", "Gold", "Platinum", "Plan cliente"];

  const PLANES_RANKING_ORDER = ["Elite", "Gold", "Platinum", "Plan cliente"];

  const ACCESORIO_GARANTIA_TIPOS_ORDER = ["accesorios", "garantias"];

  const pick = (...values) => {
    for (const value of values) {
      if (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
      ) {
        return value;
      }
    }
    return "";
  };

  const normalizeText = (value) => String(value || "").trim();

  const safeJsonParse = (value, fallback = null) => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "object") return value;

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  };

  const normalizeStatus = (value) => {
    const raw = normalizeText(value);
    const v = raw
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (!v) return "Sin status";
    if (v === "sin status") return "Sin status";
    if (v === "autorizado") return "Autorizado";
    if (v === "rechazado") return "Rechazado";
    if (v === "pendiente de buro") return "Pendiente de Buro";
    if (v === "cotizacion") return "Cotización";
    if (v === "formalizado") return "Formalizado";

    return "Sin status";
  };

  const normalizeSubStatus = (value) => {
    const raw = normalizeText(value);
    const v = raw
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (!v) return "Sin substatus";
    if (v === "sin substatus") return "Sin substatus";
    if (v === "analisis") return "Analisis";
    if (v === "aprobado sin formalizar") return "Aprobado Sin Formalizar";
    if (v === "aprobado sin finalizar") return "Aprobado Sin Formalizar";
    if (v === "condicionado viable") return "Condicionado Viable";
    if (v === "condicionado no viable") return "Condicionado No Viable";
    if (v === "facturado") return "Facturado";
    if (v === "formalizado") return "Formalizado";
    if (v === "incompleto") return "Incompleto";
    if (v === "rechazado") return "Rechazado";

    return "Sin substatus";
  };

  const normalizeFuente = (value) => normalizeText(value) || "Digital";
  const normalizeFinanciera = (value) => normalizeText(value) || "(En blanco)";
  const normalizeAgencia = (value) => normalizeText(value) || "(En blanco)";
  const normalizeDireccion = (value) => normalizeText(value) || "(En blanco)";
  const normalizeRazonSocial = (value) => normalizeText(value) || "(En blanco)";
  const normalizeAsesor = (value) => normalizeText(value) || "EXTERNO";

  const normalizeTipoPago = (value) => {
    const raw = normalizeText(value).toLowerCase();

    if (raw === "si") return "Contado";
    if (raw === "no") return "Crédito";

    return "Crédito";
  };

  const normalizeDepartamento = (value, item = null) => {
    const raw = normalizeText(value || "");
    const v = raw.toLowerCase();

    if (v.includes("semi")) return "Seminuevos";
    if (v.includes("nuevo")) return "Nuevos";

    const tipoAuto = normalizeText(
      pick(item?.tipo_auto, item?.tipo, item?.departamento),
    ).toLowerCase();

    if (tipoAuto.includes("semi")) return "Seminuevos";
    if (tipoAuto.includes("nuevo")) return "Nuevos";

    return "(En blanco)";
  };

  const normalizePlanTitle = (value) => {
    const raw = normalizeText(value);
    const v = raw.toLowerCase();

    if (!v) return "Sin plan";
    if (v === "elite") return "Elite";
    if (v === "gold") return "Gold";
    if (v === "platinum") return "Platinum";
    if (v === "plan cliente") return "Plan cliente";

    return raw;
  };

  const normalizeAccessoryTitle = (value) => {
    return normalizeText(value) || "Sin nombre";
  };

  const parsePlanesFinanciamiento = (value) => {
    const parsed = safeJsonParse(value, []);
    if (!Array.isArray(parsed)) return [];

    return parsed.map((plan) => ({
      ...plan,
      title: normalizePlanTitle(plan?.title),
      isSelected: Boolean(plan?.isSelected),
      accessories: Array.isArray(plan?.accessories)
        ? plan.accessories.map((item) => ({
            ...item,
            title: normalizeAccessoryTitle(item?.title),
            type: normalizeText(item?.type)?.toLowerCase() || "sin tipo",
          }))
        : [],
    }));
  };

  const parseDate = (item) => {
    const raw = pick(
      item?.fecha,
      item?.fecha_registro,
      item?.created_at,
      item?.fecha_creacion,
      item?.createdAt,
    );

    if (!raw) return null;

    const m = Moment(
      raw,
      [
        "YYYY-MM-DD",
        "YYYY-MM-DD HH:mm:ss",
        "DD/MM/YYYY",
        "DD/MM/YYYY HH:mm:ss",
        "MM/DD/YYYY",
        "MM/DD/YYYY HH:mm:ss",
        Moment.ISO_8601,
      ],
      true,
    );

    if (m.isValid()) return m;

    const m2 = Moment(raw);
    return m2.isValid() ? m2 : null;
  };

  const normalizePruebaManejo = (value) => {
    const parsed = safeJsonParse(value, null);
    const raw = normalizeText(
      parsed?.realizo_prueba_manejo ??
        parsed?.realizoPruebaManejo ??
        parsed?.realizo ??
        value,
    );

    const normalized = raw
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (["si", "sí", "yes", "true", "1"].includes(normalized)) {
      return "Sí";
    }

    return "No";
  };

  const normalizeRow = (item) => {
    const planesFinanciamiento = parsePlanesFinanciamiento(
      pick(item?.planes_financiamiento, item?.planesFinanciamiento),
    );

    const selectedPlans = planesFinanciamiento.filter(
      (plan) => plan.isSelected,
    );

    const selectedAccessories = selectedPlans.flatMap((plan) =>
      Array.isArray(plan.accessories) ? plan.accessories : [],
    );

    return {
      ...item,
      _status: normalizeStatus(item?.status),
      _subStatus: normalizeSubStatus(item?.sub_status),
      _tipoPago: normalizeTipoPago(
        pick(item?.depende_auto, item?.depente_auto),
      ),
      _fuente: normalizeFuente(
        pick(item?.fuente, item?.origen_lead, item?.lead_source),
      ),
      _financiera: normalizeFinanciera(
        pick(item?.financiera, item?.financiera_nombre),
      ),
      _agencia: normalizeAgencia(pick(item?.agencia, item?.agency)),
      _departamento: normalizeDepartamento(
        pick(item?.departamento, item?.tipo_auto),
        item,
      ),
      _direccion: normalizeDireccion(pick(item?.direccion, item?.director)),
      _razonSocial: normalizeRazonSocial(
        pick(item?.razon_social, item?.razonSocial, item?.empresa),
      ),
      _asesor: normalizeAsesor(
        pick(item?.asesor, item?.nombre_asesor, item?.asesor_nombre),
      ),
      _fecha: parseDate(item),
      _realizoPruebaManejo: normalizePruebaManejo(
        pick(item?.prueba_manejo, item?.pruebaManejo),
      ),
      _planesFinanciamiento: planesFinanciamiento,
      _selectedPlanTitles: selectedPlans.map((plan) => plan.title),
      _selectedAccessories: selectedAccessories,
    };
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const agenciasStorage =
        JSON.parse(localStorage.getItem("agencias")) || [];
      const modulos = JSON.parse(localStorage.getItem("modulos")) || [];
      const userId = localStorage.getItem("userId");

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: accessToken,
          modulos,
          usuario_id: userId,
          agencias: agenciasStorage,
        }),
      });

      const data = await response.json();

      if (data?.statusCode === 200 && Array.isArray(data?.data?.resultados)) {
        const normalized = data.data.resultados.map(normalizeRow);
        setRawData(normalized);
      } else if (data?.statusCode === 404) {
        setRawData([]);
        Swal.fire({
          icon: "warning",
          title: "Sin resultados",
          text: "No se encontraron resultados.",
        });
      } else {
        setRawData([]);
        console.error("Respuesta no esperada:", data);
      }
    } catch (error) {
      setRawData([]);
      console.error("Error al consultar:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const cancelButton = document.querySelector(".cancelBtn");
      const applyButton = document.querySelector(".applyBtn");

      if (cancelButton && cancelButton.textContent !== "Cancelar") {
        cancelButton.textContent = "Cancelar";
      }
      if (applyButton && applyButton.textContent !== "Aplicar") {
        applyButton.textContent = "Aplicar";
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const handleDateApplyEvent = (event, picker) => {
    const startDate = picker.startDate.format("YYYY-MM-DD");
    const endDate = picker.endDate.format("YYYY-MM-DD");

    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
    }));
  };

  const resetPickerUi = () => {
    if (datePickerRef.current?.picker) {
      const picker = datePickerRef.current.picker;
      const today = Moment();

      picker.setStartDate(today.clone().startOf("month"));
      picker.setEndDate(today.clone());
      picker.updateView();
      picker.updateCalendars();
      picker.updateElement();
    }

    setDatePickerResetKey((prev) => prev + 1);
  };

  const clearDateFilter = () => {
    setFilters((prev) => ({
      ...prev,
      startDate: "",
      endDate: "",
    }));

    resetPickerUi();
  };

  const clearAllFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      direccion: "Todas",
      razonSocial: "Todas",
      agencia: "Todas",
      departamento: "Todas",
      tipoPago: "Todas",
    });

    resetPickerUi();
  };

  const selectOptions = useMemo(() => {
    const uniq = (key) => [
      "Todas",
      ...Array.from(
        new Set(rawData.map((item) => item[key]).filter(Boolean)),
      ).sort((a, b) => String(a).localeCompare(String(b))),
    ];

    return {
      direccion: uniq("_direccion"),
      razonSocial: uniq("_razonSocial"),
      agencia: uniq("_agencia"),
      departamento: uniq("_departamento"),
    };
  }, [rawData]);

  const filteredData = useMemo(() => {
    return rawData.filter((item) => {
      if (filters.agencia !== "Todas" && item._agencia !== filters.agencia) {
        return false;
      }

      if (
        filters.direccion !== "Todas" &&
        item._direccion !== filters.direccion
      ) {
        return false;
      }

      if (
        filters.departamento !== "Todas" &&
        item._departamento !== filters.departamento
      ) {
        return false;
      }

      if (
        filters.razonSocial !== "Todas" &&
        item._razonSocial !== filters.razonSocial
      ) {
        return false;
      }

      if (filters.tipoPago !== "Todas" && item._tipoPago !== filters.tipoPago) {
        return false;
      }

      const hasDateFilter = Boolean(filters.startDate && filters.endDate);

      if (hasDateFilter) {
        if (!item._fecha || !item._fecha.isValid()) return false;

        const start = Moment(filters.startDate, "YYYY-MM-DD", true).startOf(
          "day",
        );
        const end = Moment(filters.endDate, "YYYY-MM-DD", true).endOf("day");

        if (!start.isValid() || !end.isValid()) return true;

        if (!item._fecha.isBetween(start, end, undefined, "[]")) {
          return false;
        }
      }

      return true;
    });
  }, [rawData, filters]);

  const isCredito = (item) => item?._tipoPago === "Crédito";
  const isContado = (item) => item?._tipoPago === "Contado";

  const hasStatusReal = (item) => {
    return item?._status && item._status !== "Sin status";
  };

  const dataConStatus = useMemo(() => {
    return filteredData.filter(hasStatusReal);
  }, [filteredData]);

  const isAutorizada = (item) => {
    return isCredito(item) && item._status === "Autorizado";
  };

  const isRechazada = (item) => {
    return (
      isCredito(item) &&
      (item._status === "Rechazado" || item._subStatus === "Rechazado")
    );
  };

  const isFormalizada = (item) => {
    return (
      (isCredito(item) && item._subStatus === "Formalizado") ||
      (isContado(item) && item._status === "Formalizado")
    );
  };

  const metrics = useMemo(() => {
    // El primer indicador sí debe considerar todas las solicitudes filtradas,
    // aunque todavía no tengan status. Esto permite comparar contra las que
    // ya tienen status asignado y saber cuántas faltan por atender.
    const total = filteredData.length;
    const conStatus = dataConStatus.length;
    const totalCredito = dataConStatus.filter(isCredito).length;
    const totalContado = dataConStatus.filter(isContado).length;
    const autorizadas = dataConStatus.filter(isAutorizada).length;
    const rechazadas = dataConStatus.filter(isRechazada).length;
    const formalizadas = dataConStatus.filter(isFormalizada).length;
    const pruebasManejoSi = dataConStatus.filter(
      (item) => item._realizoPruebaManejo === "Sí",
    ).length;
    const pruebasManejoNo = dataConStatus.filter(
      (item) => item._realizoPruebaManejo !== "Sí",
    ).length;

    return {
      total,
      conStatus,
      totalCredito,
      totalContado,
      autorizadas,
      rechazadas,
      formalizadas,
      pruebasManejoSi,
      pruebasManejoNo,
      pendientesStatus: total - conStatus,
      pctSolicitudesTotales: total
        ? ((conStatus / total) * 100).toFixed(1)
        : "0.0",
      pctPruebasManejo: conStatus
        ? ((pruebasManejoSi / conStatus) * 100).toFixed(1)
        : "0.0",
      pctAprobacion: totalCredito
        ? ((autorizadas / totalCredito) * 100).toFixed(1)
        : "0.0",
      pctRechazo: totalCredito
        ? ((rechazadas / totalCredito) * 100).toFixed(1)
        : "0.0",
      pctFormalizacion: conStatus
        ? ((formalizadas / conStatus) * 100).toFixed(1)
        : "0.0",
    };
  }, [filteredData, dataConStatus]);

  const formatNumber = (num) => new Intl.NumberFormat("es-MX").format(num);

  const formatCurrency = (num) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 2,
    }).format(Number(num || 0));

  const parseMoneyValue = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;

    let cleaned = String(value)
      .replace(/\s/g, "")
      .replace(/[^0-9,.-]/g, "");

    if (!cleaned) return 0;

    const hasComma = cleaned.includes(",");
    const hasDot = cleaned.includes(".");

    if (hasComma && hasDot) {
      cleaned = cleaned.replace(/,/g, "");
    } else if (hasComma && !hasDot) {
      cleaned = cleaned.replace(/,/g, ".");
    }

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getAccessoryAmount = (item) => {
    return parseMoneyValue(
      pick(
        item?.utilidad,
        item?.ganancia,
        item?.profit,
        item?.price_promo,
        item?.precio_promo,
        item?.price,
        item?.precio,
        item?.price_base,
        item?.precio_base,
        item?.amount,
        item?.monto,
        item?.total,
      ),
    );
  };

  const truncateText = (value, max = 18) => {
    const text = String(value || "");
    return text.length > max ? `${text.slice(0, max)}…` : text;
  };

  const groupTopNWithOthers = (items = [], topN = 6) => {
    const sorted = [...items].sort((a, b) => b.y - a.y);
    const top = sorted.slice(0, topN);
    const others = sorted.slice(topN);

    if (others.length > 0) {
      const othersTotal = others.reduce((acc, item) => acc + item.y, 0);
      top.push({
        name: "Otros",
        y: othersTotal,
      });
    }

    return top;
  };

  const percentage = (value, total) => {
    if (!total) return "0.0";
    return ((value / total) * 100).toFixed(1);
  };

  const getNoDataOptions = (height = 320) => ({
    chart: {
      type: "column",
      backgroundColor: chartBg,
      height,
    },
    title: {
      text: "Sin datos para mostrar",
      style: {
        color: mutedColor,
        fontSize: "14px",
        fontWeight: 600,
      },
    },
    credits: { enabled: false },
    xAxis: { visible: false },
    yAxis: { visible: false },
    legend: { enabled: false },
    series: [],
  });

  const dataForRealRange = useMemo(() => {
    return rawData.filter((item) => {
      if (filters.agencia !== "Todas" && item._agencia !== filters.agencia) {
        return false;
      }

      if (
        filters.direccion !== "Todas" &&
        item._direccion !== filters.direccion
      ) {
        return false;
      }

      if (
        filters.departamento !== "Todas" &&
        item._departamento !== filters.departamento
      ) {
        return false;
      }

      if (
        filters.razonSocial !== "Todas" &&
        item._razonSocial !== filters.razonSocial
      ) {
        return false;
      }

      if (filters.tipoPago !== "Todas" && item._tipoPago !== filters.tipoPago) {
        return false;
      }

      return hasStatusReal(item) && item._fecha && item._fecha.isValid();
    });
  }, [
    rawData,
    filters.agencia,
    filters.direccion,
    filters.departamento,
    filters.razonSocial,
    filters.tipoPago,
  ]);

  const realDateRange = useMemo(() => {
    if (!dataForRealRange.length) {
      return { min: null, max: null };
    }

    const sortedDates = dataForRealRange
      .map((item) => item._fecha.clone())
      .sort((a, b) => a.valueOf() - b.valueOf());

    return {
      min: sortedDates[0],
      max: sortedDates[sortedDates.length - 1],
    };
  }, [dataForRealRange]);

  const selectedDateRangeText =
    filters.startDate && filters.endDate
      ? `${Moment(filters.startDate).format("DD/MM/YYYY")} a ${Moment(filters.endDate).format("DD/MM/YYYY")}`
      : realDateRange.min && realDateRange.max
        ? `${realDateRange.min.format("DD/MM/YYYY")} a ${realDateRange.max.format("DD/MM/YYYY")}`
        : "Sin fechas disponibles";

  const chartRenderKey = useMemo(() => {
    return [
      dataConStatus.length,
      filters.startDate || "sin-inicio",
      filters.endDate || "sin-fin",
      filters.agencia,
      filters.direccion,
      filters.departamento,
      filters.razonSocial,
      filters.tipoPago,
    ].join("|");
  }, [
    dataConStatus.length,
    filters.startDate,
    filters.endDate,
    filters.agencia,
    filters.direccion,
    filters.departamento,
    filters.razonSocial,
    filters.tipoPago,
  ]);

  const statusChartData = useMemo(() => {
    if (filters.tipoPago === "Contado") return [];

    return dataConStatus.filter((item) => item._tipoPago === "Crédito");
  }, [dataConStatus, filters.tipoPago]);

  const contadoStatusChartData = useMemo(() => {
    if (filters.tipoPago === "Crédito") return [];

    return dataConStatus.filter((item) => item._tipoPago === "Contado");
  }, [dataConStatus, filters.tipoPago]);

  const shouldShowStatusChart = filters.tipoPago !== "Contado";
  const shouldShowContadoStatusChart = filters.tipoPago !== "Crédito";
  const shouldShowCreditOnlyBlocks = filters.tipoPago !== "Contado";

  const statusSubstatusChartOptions = useMemo(() => {
    if (!statusChartData.length) return getNoDataOptions(420);

    const statuses = STATUS_ORDER;
    const orderedSubstatuses = SUBSTATUS_ORDER;

    const substatusColors = {
      "Sin substatus": colorSet.gris,
      Analisis: colorSet.azulOscuro,
      "Condicionado Viable": colorSet.azul,
      "Condicionado No Viable": colorSet.rojo,
      Incompleto: colorSet.amarillo,
      Facturado: colorSet.celeste,
      Formalizado: colorSet.turquesa,
      "Aprobado Sin Formalizar": colorSet.naranja,
      Rechazado: colorSet.rosa,
    };

    const totalsByStatus = statuses.map(
      (status) =>
        statusChartData.filter((item) => item._status === status).length,
    );

    const series = orderedSubstatuses
      .map((sub) => ({
        name: sub,
        color: substatusColors[sub] || colorSet.gris,
        data: statuses.map((status) => {
          return statusChartData.filter(
            (item) => item._status === status && item._subStatus === sub,
          ).length;
        }),
      }))
      .filter((serie) => serie.data.some((value) => value > 0));

    if (!series.length) return getNoDataOptions(420);

    return {
      chart: {
        type: "bar",
        backgroundColor: chartBg,
        height: 420,
      },
      title: { text: null },
      credits: { enabled: false },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      xAxis: {
        categories: statuses,
        labels: {
          style: {
            color: textColor,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Total solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        stackLabels: {
          enabled: true,
          style: {
            color: textColor,
            textOutline: "none",
            fontWeight: 700,
          },
        },
        allowDecimals: false,
      },
      tooltip: {
        shared: true,
        formatter: function () {
          const total = totalsByStatus[this.points[0].point.index] || 0;
          let html = `<b>${this.x}</b><br/>Total: <b>${total}</b><br/><br/>`;

          this.points.forEach((point) => {
            if (point.y > 0) {
              html += `
                <span style="color:${point.color}">\u25CF</span>
                ${point.series.name}: <b>${point.y}</b> (${percentage(point.y, total)}%)<br/>
              `;
            }
          });

          return html;
        },
      },
      plotOptions: {
        series: {
          stacking: "normal",
          borderWidth: 0,
          pointPadding: 0.1,
          groupPadding: 0.08,
          dataLabels: {
            enabled: false,
          },
        },
      },
      series,
    };
  }, [statusChartData, textColor, gridColor, mutedColor]);

  const contadoStatusChartOptions = useMemo(() => {
    if (!contadoStatusChartData.length) return getNoDataOptions(340);

    const statuses = CONTADO_STATUS_ORDER;
    const counts = statuses.map((status) => ({
      name: status,
      y: contadoStatusChartData.filter((item) => item._status === status)
        .length,
      color: status === "Formalizado" ? colorSet.turquesa : colorSet.naranja,
    }));

    const hasValues = counts.some((item) => item.y > 0);
    if (!hasValues) return getNoDataOptions(340);

    const total = contadoStatusChartData.length;

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 340,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: textColor,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Total solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${this.key}</b><br/>Solicitudes: <b>${this.y}</b><br/>${percentage(this.y, total)}% del contado`;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            formatter: function () {
              return `${this.y}`;
            },
            style: {
              textOutline: "none",
              fontWeight: 700,
            },
          },
        },
      },
      series: [
        {
          name: "Status contado",
          data: counts,
        },
      ],
    };
  }, [contadoStatusChartData, textColor, gridColor]);

  const fuenteChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(380);

    const counts = {};
    dataConStatus.forEach((item) => {
      counts[item._fuente] = (counts[item._fuente] || 0) + 1;
    });

    const rawFuenteData = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, y]) => ({ name, y }));

    const data = groupTopNWithOthers(rawFuenteData, 6);

    return {
      chart: {
        type: "pie",
        backgroundColor: chartBg,
        height: 380,
      },
      title: { text: null },
      subtitle: {
        text: `Total: ${formatNumber(dataConStatus.length)}`,
        align: "center",
        verticalAlign: "middle",
        y: 10,
        style: {
          color: textColor,
          fontSize: "16px",
          fontWeight: 700,
        },
      },
      credits: { enabled: false },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        pointFormat: "<b>{point.y}</b> solicitudes ({point.percentage:.1f}%)",
      },
      plotOptions: {
        pie: {
          innerSize: "62%",
          showInLegend: true,
          dataLabels: {
            enabled: true,
            formatter: function () {
              if (this.y <= 0) return null;
              return this.percentage >= 6
                ? `${this.point.name}<br/>${this.y} (${this.percentage.toFixed(1)}%)`
                : null;
            },
            style: {
              textOutline: "none",
              color: textColor,
              fontSize: "10px",
            },
          },
        },
      },
      series: [
        {
          name: "Fuente",
          data,
        },
      ],
    };
  }, [dataConStatus, textColor]);

  const financieraChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(380);

    const counts = {};
    dataConStatus.forEach((item) => {
      counts[item._financiera] = (counts[item._financiera] || 0) + 1;
    });

    const rawFinancieraData = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, y]) => ({ name, y }));

    const data = groupTopNWithOthers(rawFinancieraData, 6);

    return {
      chart: {
        type: "pie",
        backgroundColor: chartBg,
        height: 380,
      },
      title: { text: null },
      subtitle: {
        text: `Total: ${formatNumber(dataConStatus.length)}`,
        align: "center",
        verticalAlign: "middle",
        y: 10,
        style: {
          color: textColor,
          fontSize: "16px",
          fontWeight: 700,
        },
      },
      credits: { enabled: false },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        pointFormat: "<b>{point.y}</b> solicitudes ({point.percentage:.1f}%)",
      },
      plotOptions: {
        pie: {
          innerSize: "62%",
          showInLegend: true,
          dataLabels: {
            enabled: true,
            formatter: function () {
              if (this.y <= 0) return null;
              return this.percentage >= 6
                ? `${this.point.name}<br/>${this.y} (${this.percentage.toFixed(1)}%)`
                : null;
            },
            style: {
              textOutline: "none",
              color: textColor,
              fontSize: "10px",
            },
          },
        },
      },
      series: [
        {
          name: "Financiera",
          data,
        },
      ],
    };
  }, [dataConStatus, textColor]);

  const tipoPagoComparacionChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(360);

    const contado = dataConStatus.filter(
      (item) => item._tipoPago === "Contado",
    ).length;

    const credito = dataConStatus.filter(
      (item) => item._tipoPago === "Crédito",
    ).length;

    const total = contado + credito;

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 360,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: ["Contado", "Crédito"],
        labels: {
          style: {
            color: textColor,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Total solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${this.x}</b><br/>Solicitudes: <b>${this.y}</b><br/>${percentage(this.y, total)}% del total`;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            formatter: function () {
              return `${this.y}`;
            },
            style: {
              textOutline: "none",
              fontWeight: 700,
            },
          },
        },
      },
      series: [
        {
          name: "Solicitudes",
          data: [
            { y: contado, color: colorSet.naranja },
            { y: credito, color: colorSet.azul },
          ],
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const pruebaManejoChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(360);

    const si = dataConStatus.filter(
      (item) => item._realizoPruebaManejo === "Sí",
    ).length;
    const no = dataConStatus.filter(
      (item) => item._realizoPruebaManejo !== "Sí",
    ).length;
    const total = si + no;

    const data = [
      { name: "Sí realizó", y: si, color: colorSet.turquesa },
      { name: "No realizó", y: no, color: colorSet.rojo },
    ];

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 360,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: textColor,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Total solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${this.key}</b><br/>Solicitudes: <b>${this.y}</b><br/>${percentage(this.y, total)}% del total`;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            formatter: function () {
              return `${this.y} (${percentage(this.y, total)}%)`;
            },
            style: {
              textOutline: "none",
              fontWeight: 700,
            },
          },
        },
      },
      series: [
        {
          name: "Prueba de manejo",
          data,
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const planesSeleccionadosChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(360);

    const counts = {
      Elite: 0,
      Gold: 0,
      Platinum: 0,
      "Plan cliente": 0,
    };

    dataConStatus.forEach((item) => {
      item._selectedPlanTitles.forEach((planTitle) => {
        const normalized = normalizePlanTitle(planTitle);
        counts[normalized] = (counts[normalized] || 0) + 1;
      });
    });

    const data = PLANES_ORDER.map((plan) => ({
      name: plan,
      y: counts[plan] || 0,
      color:
        plan === "Elite"
          ? colorSet.azulOscuro
          : plan === "Gold"
            ? colorSet.amarillo
            : plan === "Platinum"
              ? colorSet.morado
              : colorSet.turquesa,
    }));

    const hasValues = data.some((item) => item.y > 0);
    if (!hasValues) return getNoDataOptions(360);

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 360,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Selecciones",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          const pct = percentage(this.y, dataConStatus.length);
          return `<b>${this.key}</b><br/>Selecciones: <b>${this.y}</b><br/>${pct}% de las solicitudes filtradas`;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
              fontWeight: 700,
            },
          },
        },
      },
      series: [
        {
          name: "Planes",
          data,
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const topAccesoriosGarantiasChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(380);

    const counts = {};

    dataConStatus.forEach((item) => {
      item._selectedAccessories.forEach((acc) => {
        const title = normalizeAccessoryTitle(acc?.title);
        const type = normalizeText(acc?.type || "sin tipo").toLowerCase();
        const key = `${title}|||${type}`;

        if (!counts[key]) {
          counts[key] = {
            name: title,
            type,
            y: 0,
          };
        }

        counts[key].y += 1;
      });
    });

    const data = Object.values(counts)
      .sort((a, b) => b.y - a.y)
      .slice(0, 5)
      .map((item) => ({
        ...item,
        color:
          item.type === "garantias"
            ? colorSet.naranja
            : item.type === "accesorios"
              ? colorSet.azul
              : colorSet.gris,
      }));

    if (!data.length) return getNoDataOptions(380);

    return {
      chart: {
        type: "bar",
        backgroundColor: chartBg,
        height: 380,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: data.map((item) => truncateText(item.name, 42)),
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Selecciones",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          const row = data[this.point.index];
          return `
            <b>${row?.name || this.category}</b><br/>
            Tipo: <b>${row?.type || "sin tipo"}</b><br/>
            Selecciones: <b>${this.y}</b>
          `;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
            },
          },
        },
      },
      series: [
        {
          name: "Selecciones",
          data: data.map((item) => ({
            y: item.y,
            color: item.color,
          })),
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const gananciasAccesoriosGarantiasResumen = useMemo(() => {
    const endBase = filters.endDate
      ? Moment(filters.endDate, "YYYY-MM-DD", true)
      : Moment();

    const baseDate = endBase.isValid() ? endBase : Moment();
    const currentStart = baseDate.clone().startOf("month");
    const currentEnd = baseDate.clone().endOf("month");
    const previousStart = baseDate
      .clone()
      .subtract(1, "month")
      .startOf("month");
    const previousEnd = baseDate.clone().subtract(1, "month").endOf("month");

    const emptyTotals = () => ({
      accesorios: 0,
      garantias: 0,
      total: 0,
      accesoriosCount: 0,
      garantiasCount: 0,
      totalCount: 0,
    });

    const getTotals = (items = []) => {
      const totals = emptyTotals();

      items.forEach((item) => {
        const selectedItems = Array.isArray(item._selectedAccessories)
          ? item._selectedAccessories
          : [];

        selectedItems.forEach((acc) => {
          const type = normalizeText(acc?.type || "sin tipo").toLowerCase();

          if (!ACCESORIO_GARANTIA_TIPOS_ORDER.includes(type)) return;

          const amount = getAccessoryAmount(acc);

          totals[type] += amount;
          totals.total += amount;
          totals[`${type}Count`] += 1;
          totals.totalCount += 1;
        });
      });

      return totals;
    };

    const currentData = dataForRealRange.filter((item) => {
      return item._fecha.isBetween(currentStart, currentEnd, undefined, "[]");
    });

    const previousData = dataForRealRange.filter((item) => {
      return item._fecha.isBetween(previousStart, previousEnd, undefined, "[]");
    });

    const currentTotals = getTotals(currentData);
    const previousTotals = getTotals(previousData);

    const diffTotal = currentTotals.total - previousTotals.total;
    const diffPct = previousTotals.total
      ? ((diffTotal / previousTotals.total) * 100).toFixed(1)
      : currentTotals.total
        ? "100.0"
        : "0.0";

    return {
      currentLabel: currentStart.format("MMMM YYYY"),
      previousLabel: previousStart.format("MMMM YYYY"),
      currentTotals,
      previousTotals,
      diffTotal,
      diffPct,
      currentStart,
      currentEnd,
      previousStart,
      previousEnd,
    };
  }, [dataForRealRange, filters.endDate]);

  const gananciasAccesoriosGarantiasRows = useMemo(() => {
    const { currentTotals, previousTotals } =
      gananciasAccesoriosGarantiasResumen;

    return [
      {
        label: "Accesorios",
        key: "accesorios",
        current: currentTotals.accesorios,
        previous: previousTotals.accesorios,
        currentCount: currentTotals.accesoriosCount,
        previousCount: previousTotals.accesoriosCount,
      },
      {
        label: "Garantías",
        key: "garantias",
        current: currentTotals.garantias,
        previous: previousTotals.garantias,
        currentCount: currentTotals.garantiasCount,
        previousCount: previousTotals.garantiasCount,
      },
      {
        label: "Total",
        key: "total",
        current: currentTotals.total,
        previous: previousTotals.total,
        currentCount: currentTotals.totalCount,
        previousCount: previousTotals.totalCount,
      },
    ].map((row) => {
      const diff = row.current - row.previous;
      const diffPct = row.previous
        ? ((diff / row.previous) * 100).toFixed(1)
        : row.current
          ? "100.0"
          : "0.0";

      return {
        ...row,
        diff,
        diffPct,
      };
    });
  }, [gananciasAccesoriosGarantiasResumen]);

  const gananciasAccesoriosGarantiasChartOptions = useMemo(() => {
    const resumen = gananciasAccesoriosGarantiasResumen;
    const hasValues = gananciasAccesoriosGarantiasRows.some(
      (row) => row.current > 0 || row.previous > 0,
    );

    if (!hasValues) return getNoDataOptions(390);

    const categories = gananciasAccesoriosGarantiasRows.map((row) => row.label);

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 390,
      },
      title: { text: null },
      subtitle: {
        text: `Comparativo ${resumen.previousLabel} vs ${resumen.currentLabel}`,
        style: {
          color: mutedColor,
          fontSize: "12px",
          fontWeight: 600,
        },
      },
      credits: { enabled: false },
      xAxis: {
        categories,
        labels: {
          style: {
            color: textColor,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Ganancia / monto vendido",
          style: { color: textColor },
        },
        labels: {
          formatter: function () {
            return formatCurrency(this.value);
          },
          style: { color: textColor },
        },
        gridLineColor: gridColor,
      },
      legend: {
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          const idx = this.points?.[0]?.point?.index ?? 0;
          const row = gananciasAccesoriosGarantiasRows[idx];
          if (!row) return false;

          let html = `<b>${row.label}</b><br/>`;

          this.points.forEach((point) => {
            html += `
              <span style="color:${point.color}">\u25CF</span>
              ${point.series.name}: <b>${formatCurrency(point.y)}</b><br/>
            `;
          });

          html += `<br/>Diferencia: <b>${formatCurrency(row.diff)}</b> (${row.diffPct}%)`;
          html += `<br/>Productos vendidos: <b>${formatNumber(row.currentCount)}</b> actual vs <b>${formatNumber(row.previousCount)}</b> anterior`;

          return html;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            formatter: function () {
              return this.y > 0 ? formatCurrency(this.y) : null;
            },
            style: {
              textOutline: "none",
              fontWeight: 700,
              fontSize: "10px",
            },
          },
        },
      },
      series: [
        {
          name: resumen.previousLabel,
          color: colorSet.gris,
          data: gananciasAccesoriosGarantiasRows.map((row) => row.previous),
        },
        {
          name: resumen.currentLabel,
          color: colorSet.azul,
          data: gananciasAccesoriosGarantiasRows.map((row) => row.current),
        },
      ],
    };
  }, [
    gananciasAccesoriosGarantiasResumen,
    gananciasAccesoriosGarantiasRows,
    textColor,
    mutedColor,
    gridColor,
  ]);

  const agenciaDepartamentoChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(380);

    const agencias = Array.from(
      new Set(dataConStatus.map((item) => item._agencia)),
    );

    const departamentos = ["(En blanco)", "Nuevos", "Seminuevos"];
    const departmentColors = {
      "(En blanco)": colorSet.azulOscuro,
      Nuevos: colorSet.naranja,
      Seminuevos: colorSet.celeste,
    };

    const series = departamentos.map((dep) => ({
      name: dep,
      color: departmentColors[dep],
      data: agencias.map((agencia) => {
        return dataConStatus.filter(
          (item) => item._agencia === agencia && item._departamento === dep,
        ).length;
      }),
    }));

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 380,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: agencias,
        labels: {
          rotation: -35,
          style: {
            color: textColor,
            fontSize: "10px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Total solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        stackLabels: {
          enabled: true,
          style: {
            color: textColor,
            textOutline: "none",
          },
        },
        allowDecimals: false,
      },
      legend: {
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        shared: true,
      },
      plotOptions: {
        column: {
          stacking: "normal",
          borderRadiusTopLeft: 4,
          borderRadiusTopRight: 4,
        },
      },
      series,
    };
  }, [dataConStatus, textColor, gridColor]);

  const acumuladoChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(360);

    const endBase = filters.endDate
      ? Moment(filters.endDate, "YYYY-MM-DD")
      : Moment();

    const monthCurrent = endBase.clone().month();
    const yearCurrent = endBase.clone().year();

    const prev = endBase.clone().subtract(1, "month");
    const monthPrev = prev.month();
    const yearPrev = prev.year();

    const daysInCurrentMonth = endBase.daysInMonth();
    const currentDaily = Array(daysInCurrentMonth).fill(0);
    const prevDaily = Array(daysInCurrentMonth).fill(0);

    dataConStatus.forEach((item) => {
      if (!item._fecha) return;

      const day = item._fecha.date();

      if (
        item._fecha.month() === monthCurrent &&
        item._fecha.year() === yearCurrent &&
        day >= 1 &&
        day <= daysInCurrentMonth
      ) {
        currentDaily[day - 1] += 1;
      }

      if (
        item._fecha.month() === monthPrev &&
        item._fecha.year() === yearPrev &&
        day >= 1 &&
        day <= daysInCurrentMonth
      ) {
        prevDaily[day - 1] += 1;
      }
    });

    const accumulated = (arr) => {
      let sum = 0;
      return arr.map((v) => {
        sum += v;
        return sum;
      });
    };

    return {
      chart: {
        type: "line",
        backgroundColor: chartBg,
        height: 360,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: Array.from({ length: daysInCurrentMonth }, (_, i) =>
          String(i + 1),
        ),
        labels: {
          style: { color: textColor },
        },
        lineColor: gridColor,
      },
      yAxis: {
        title: {
          text: "Acumulado",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: {
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        shared: true,
      },
      plotOptions: {
        series: {
          marker: {
            enabled: true,
            radius: 3,
          },
          lineWidth: 3,
        },
      },
      series: [
        {
          name: "Acumulado Mes Actual",
          color: colorSet.azulOscuro,
          data: accumulated(currentDaily),
        },
        {
          name: "Acumulado Mes Anterior",
          color: colorSet.azul,
          data: accumulated(prevDaily),
        },
      ],
    };
  }, [dataConStatus, filters.endDate, textColor, gridColor]);

  const asesorRows = useMemo(() => {
    // dataConStatus ya respeta los filtros actuales, incluyendo tipo de pago.
    // Cuando el filtro está en "Todas", aquí deben entrar crédito y contado,
    // para que el total por asesor coincida con el total real de solicitudes con status.
    const baseData = dataConStatus;

    const total = baseData.length || 1;
    const map = {};

    baseData.forEach((item) => {
      const asesor = item._asesor;

      if (!map[asesor]) {
        map[asesor] = {
          asesor,
          total: 0,
          creditos: 0,
          contados: 0,
          autorizadas: 0,
          rechazadas: 0,
          pendientesBuro: 0,
          formalizadas: 0,
          pruebasManejo: 0,
        };
      }

      map[asesor].total += 1;

      if (isCredito(item)) map[asesor].creditos += 1;
      if (isContado(item)) map[asesor].contados += 1;
      if (isAutorizada(item)) map[asesor].autorizadas += 1;
      if (isRechazada(item)) map[asesor].rechazadas += 1;
      if (item._status === "Pendiente de Buro") map[asesor].pendientesBuro += 1;
      if (isFormalizada(item)) map[asesor].formalizadas += 1;
      if (item._realizoPruebaManejo === "Sí") map[asesor].pruebasManejo += 1;
    });

    return Object.values(map)
      .map((row) => ({
        ...row,
        pctAutorizadas: row.creditos
          ? ((row.autorizadas / row.creditos) * 100).toFixed(1)
          : "0.0",
        pctRechazadas: row.creditos
          ? ((row.rechazadas / row.creditos) * 100).toFixed(1)
          : "0.0",
        pctParticipacion: ((row.total / total) * 100).toFixed(1),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 15);
  }, [dataConStatus]);

  const asesorPaquetesRows = useMemo(() => {
    const map = {};

    dataConStatus.forEach((item) => {
      const asesor = item._asesor || "EXTERNO";

      if (!map[asesor]) {
        map[asesor] = {
          asesor,
          totalPaquetesVendidos: 0,
        };

        PLANES_RANKING_ORDER.forEach((plan) => {
          map[asesor][plan] = 0;
        });
      }

      const selectedPlans = Array.isArray(item._selectedPlanTitles)
        ? item._selectedPlanTitles
        : [];

      selectedPlans.forEach((planTitle) => {
        const normalized = normalizePlanTitle(planTitle);

        if (!map[asesor][normalized] && map[asesor][normalized] !== 0) {
          map[asesor][normalized] = 0;
        }

        map[asesor][normalized] += 1;
        map[asesor].totalPaquetesVendidos += 1;
      });
    });

    return Object.values(map)
      .sort((a, b) => b.totalPaquetesVendidos - a.totalPaquetesVendidos)
      .slice(0, 15);
  }, [dataConStatus]);

  const accesoriosGarantiasColumns = useMemo(() => {
    const counts = {};

    dataConStatus.forEach((item) => {
      const selectedItems = Array.isArray(item._selectedAccessories)
        ? item._selectedAccessories
        : [];

      selectedItems.forEach((acc) => {
        const title = normalizeAccessoryTitle(acc?.title);
        const type = normalizeText(acc?.type || "sin tipo").toLowerCase();
        const key = `${title}|||${type}`;

        if (!counts[key]) {
          counts[key] = {
            key,
            title,
            type,
            total: 0,
          };
        }

        counts[key].total += 1;
      });
    });

    return Object.values(counts)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [dataConStatus]);

  const asesorAccesoriosGarantiasMatrixRows = useMemo(() => {
    const map = {};

    dataConStatus.forEach((item) => {
      const asesor = item._asesor || "EXTERNO";
      const selectedItems = Array.isArray(item._selectedAccessories)
        ? item._selectedAccessories
        : [];

      if (!map[asesor]) {
        map[asesor] = {
          asesor,
          totalItemsVendidos: 0,
        };
      }

      selectedItems.forEach((acc) => {
        const title = normalizeAccessoryTitle(acc?.title);
        const type = normalizeText(acc?.type || "sin tipo").toLowerCase();
        const key = `${title}|||${type}`;

        if (!(key in map[asesor])) {
          map[asesor][key] = 0;
        }

        map[asesor][key] += 1;
        map[asesor].totalItemsVendidos += 1;
      });
    });

    return Object.values(map)
      .sort((a, b) => b.totalItemsVendidos - a.totalItemsVendidos)
      .slice(0, 15);
  }, [dataConStatus]);

  const formalizacionesFinancieraChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(320);

    const counts = {};
    dataConStatus.filter(isFormalizada).forEach((item) => {
      counts[item._financiera] = (counts[item._financiera] || 0) + 1;
    });

    const data = groupTopNWithOthers(
      Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([name, y]) => ({ name, y })),
      5,
    );

    return {
      chart: {
        type: "pie",
        backgroundColor: chartBg,
        height: 320,
      },
      title: { text: null },
      credits: { enabled: false },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        pointFormat:
          "<b>{point.y}</b> formalizaciones ({point.percentage:.1f}%)",
      },
      plotOptions: {
        pie: {
          innerSize: "55%",
          dataLabels: {
            enabled: true,
            formatter: function () {
              return this.percentage >= 7
                ? `${this.point.name}<br/>${this.y} (${this.percentage.toFixed(1)}%)`
                : null;
            },
            style: {
              textOutline: "none",
              color: textColor,
              fontSize: "10px",
            },
          },
          showInLegend: true,
        },
      },
      series: [
        {
          name: "Formalizaciones",
          data,
        },
      ],
    };
  }, [dataConStatus, textColor]);

  const direccionRows = useMemo(() => {
    const map = {};

    dataConStatus.forEach((item) => {
      const key = item._direccion;

      if (!map[key]) {
        map[key] = {
          direccion: key,
          total: 0,
          autorizadas: 0,
          formalizaciones: 0,
        };
      }

      map[key].total += 1;
      if (isAutorizada(item)) map[key].autorizadas += 1;
      if (isFormalizada(item)) map[key].formalizaciones += 1;
    });

    return Object.values(map)
      .map((row) => ({
        ...row,
        pctAprobacion: row.total
          ? ((row.autorizadas / row.total) * 100).toFixed(1)
          : "0.0",
      }))
      .sort((a, b) => b.formalizaciones - a.formalizaciones)
      .slice(0, 8);
  }, [dataConStatus]);

  const statusSimpleChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(340);

    const baseData =
      filters.tipoPago === "Contado"
        ? dataConStatus.filter(isContado)
        : filters.tipoPago === "Todas"
          ? dataConStatus.filter(isCredito)
          : dataConStatus;

    const statuses =
      filters.tipoPago === "Contado" ? CONTADO_STATUS_ORDER : STATUS_ORDER;

    const data = statuses.map((status) => ({
      name: status,
      y: baseData.filter((item) => item._status === status).length,
      color:
        status === "Autorizado"
          ? colorSet.turquesa
          : status === "Rechazado"
            ? colorSet.rojo
            : status === "Pendiente de Buro"
              ? colorSet.azul
              : status === "Formalizado"
                ? colorSet.turquesa
                : status === "Cotización"
                  ? colorSet.naranja
                  : colorSet.gris,
    }));

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 340,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        pointFormat: "<b>{point.y}</b> solicitudes",
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
              fontWeight: 700,
            },
          },
        },
      },
      series: [
        {
          name: "Solicitudes",
          data,
        },
      ],
    };
  }, [dataConStatus, filters.tipoPago, textColor, gridColor]);

  const substatusSimpleChartOptions = useMemo(() => {
    const baseData =
      filters.tipoPago === "Todas"
        ? dataConStatus.filter(isCredito)
        : dataConStatus.filter(isCredito);

    if (!baseData.length) return getNoDataOptions(340);

    const data = SUBSTATUS_ORDER.map((sub) => ({
      name: sub,
      y: baseData.filter((item) => item._subStatus === sub).length,
    }));

    return {
      chart: {
        type: "bar",
        backgroundColor: chartBg,
        height: 380,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: data.map((item) => truncateText(item.name, 24)),
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${data[this.point.index]?.name || this.category}</b><br/>Solicitudes: <b>${this.y}</b>`;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
            },
          },
        },
      },
      series: [
        {
          name: "Subestatus",
          color: colorSet.morado,
          data: data.map((item) => item.y),
        },
      ],
    };
  }, [dataConStatus, filters.tipoPago, textColor, gridColor]);

  const topAgenciasChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(400);

    const counts = {};
    dataConStatus.forEach((item) => {
      counts[item._agencia] = (counts[item._agencia] || 0) + 1;
    });

    const data = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, y]) => ({
        name,
        y,
      }));

    return {
      chart: {
        type: "bar",
        backgroundColor: chartBg,
        height: 400,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: data.map((item) => truncateText(item.name, 24)),
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${data[this.point.index]?.name || this.category}</b><br/>Solicitudes: <b>${this.y}</b>`;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
            },
          },
        },
      },
      series: [
        {
          name: "Solicitudes",
          color: colorSet.azulOscuro,
          data: data.map((item) => item.y),
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const topAgenciasFormalizacionChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(400);

    const map = {};
    dataConStatus.forEach((item) => {
      const key = item._agencia;
      if (!map[key]) {
        map[key] = {
          agencia: key,
          total: 0,
          formalizadas: 0,
        };
      }
      map[key].total += 1;
      if (isFormalizada(item)) map[key].formalizadas += 1;
    });

    const data = Object.values(map)
      .sort((a, b) => b.formalizadas - a.formalizadas)
      .slice(0, 10);

    return {
      chart: {
        type: "bar",
        backgroundColor: chartBg,
        height: 400,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: data.map((item) => truncateText(item.agencia, 24)),
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Formalizaciones",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          const row = data[this.point.index];
          return `
            <b>${row?.agencia || this.category}</b><br/>
            Formalizaciones: <b>${this.y}</b><br/>
            Conversión: <b>${percentage(row?.formalizadas || 0, row?.total || 0)}%</b> (${row?.formalizadas || 0} de ${row?.total || 0})
          `;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
            },
          },
        },
      },
      series: [
        {
          name: "Formalizaciones",
          color: colorSet.naranja,
          data: data.map((item) => item.formalizadas),
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const tendenciaDiariaChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(340);

    const counts = {};

    dataConStatus.forEach((item) => {
      if (!item._fecha) return;
      const key = item._fecha.format("YYYY-MM-DD");
      counts[key] = (counts[key] || 0) + 1;
    });

    const keys = Object.keys(counts).sort((a, b) => a.localeCompare(b));

    return {
      chart: {
        type: "line",
        backgroundColor: chartBg,
        height: 340,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: keys.map((key) => Moment(key).format("DD MMM")),
        labels: {
          style: { color: textColor, fontSize: "10px" },
          rotation: -35,
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Solicitudes por día",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        pointFormat: "<b>{point.y}</b> solicitudes",
      },
      plotOptions: {
        line: {
          lineWidth: 3,
          marker: {
            enabled: true,
            radius: 3,
          },
          dataLabels: {
            enabled: false,
          },
        },
      },
      series: [
        {
          name: "Solicitudes",
          color: colorSet.morado,
          data: keys.map((key) => counts[key]),
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const funnelChartOptions = useMemo(() => {
    const totalSolicitudes = dataConStatus.length;

    if (!totalSolicitudes) return getNoDataOptions(380);

    const totalConStatus = dataConStatus.length;

    const esAutorizadaOCotizadaParaFunnel = (item) => {
      if (!item) return false;

      if (isCredito(item)) {
        return isAutorizada(item) || isFormalizada(item);
      }

      if (isContado(item)) {
        return ["Cotización", "Formalizado"].includes(item._status);
      }

      return false;
    };

    const autorizadasOCotizadas = dataConStatus.filter(
      esAutorizadaOCotizadaParaFunnel,
    ).length;
    const formalizadas = dataConStatus.filter(isFormalizada).length;
    const pruebasManejoSi = dataConStatus.filter(
      (item) => item._realizoPruebaManejo === "Sí",
    ).length;

    const autorizadasOCotizadasLabel =
      filters.tipoPago === "Crédito"
        ? "Autorizadas"
        : filters.tipoPago === "Contado"
          ? "Cotizadas"
          : "Autorizadas / cotizadas";

    const autorizadasOCotizadasDescription =
      filters.tipoPago === "Crédito"
        ? "Solicitudes de crédito con status Autorizado o ya formalizadas."
        : filters.tipoPago === "Contado"
          ? "Solicitudes de contado en Cotización o Formalizado."
          : "Créditos autorizados y solicitudes de contado cotizadas/formalizadas.";

    const stages = [
      {
        name: "Solicitudes con status",
        y: totalSolicitudes,
        color: colorSet.azulOscuro,
        description:
          "Registros con status asignado dentro del rango y filtros actuales.",
      },
      {
        name: autorizadasOCotizadasLabel,
        y: autorizadasOCotizadas,
        color: colorSet.turquesa,
        description: autorizadasOCotizadasDescription,
      },
      {
        name: "Formalizadas",
        y: formalizadas,
        color: colorSet.naranja,
        description: "Solicitudes cerradas como formalizadas.",
      },
    ].map((stage, index, arr) => {
      const previous = index > 0 ? arr[index - 1] : null;

      return {
        ...stage,
        custom: {
          description: stage.description,
          pctTotal: percentage(stage.y, totalSolicitudes),
          pctPrevious: previous ? percentage(stage.y, previous.y) : "100.0",
          previousName: previous ? previous.name : "inicio del embudo",
        },
      };
    });

    return {
      chart: {
        type: "funnel",
        backgroundColor: chartBg,
        height: 380,
        spacingTop: 10,
        spacingBottom: 10,
      },
      title: { text: null },
      subtitle: {
        text: `Prueba de manejo: ${formatNumber(pruebasManejoSi)} (${percentage(
          pruebasManejoSi,
          totalConStatus,
        )}%)`,
        style: {
          color: mutedColor,
          fontSize: "12px",
          fontWeight: 600,
        },
      },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: {
        useHTML: true,
        formatter: function () {
          const custom = this.point.custom || {};
          const previousText =
            this.point.index === 0
              ? "Etapa inicial"
              : `${custom.pctPrevious}% desde ${custom.previousName}`;

          return `
            <b>${this.point.name}</b><br/>
            Solicitudes: <b>${formatNumber(this.y)}</b><br/>
            ${custom.pctTotal}% del total<br/>
            ${previousText}<br/>
            <span style="color:${mutedColor}">${custom.description || ""}</span>
          `;
        },
      },
      plotOptions: {
        funnel: {
          width: "82%",
          height: "82%",
          neckWidth: "34%",
          neckHeight: "24%",
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            softConnector: true,
            connectorColor: gridColor,
            distance: 18,
            formatter: function () {
              const custom = this.point.custom || {};

              return `
                <span style="font-weight:700">${this.point.name}</span><br/>
                ${formatNumber(this.y)} (${custom.pctTotal}% total)
              `;
            },
            style: {
              color: textColor,
              textOutline: "none",
              fontSize: "11px",
            },
          },
        },
        series: {
          animation: { duration: 450 },
          states: {
            inactive: { opacity: 0.85 },
          },
        },
      },
      series: [
        {
          name: "Embudo de conversión",
          data: stages,
        },
      ],
    };
  }, [dataConStatus, filters.tipoPago, textColor, mutedColor, gridColor]);

  const conversionAgenciaChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(400);

    const map = {};
    dataConStatus.forEach((item) => {
      const key = item._agencia;
      if (!map[key]) {
        map[key] = {
          agencia: key,
          autorizadas: 0,
          formalizadas: 0,
          total: 0,
        };
      }
      map[key].total += 1;
      if (isAutorizada(item)) map[key].autorizadas += 1;
      if (isFormalizada(item)) map[key].formalizadas += 1;
    });

    const data = Object.values(map)
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 400,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: data.map((item) => truncateText(item.agencia, 18)),
        labels: {
          rotation: -20,
          style: {
            color: textColor,
            fontSize: "10px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Cantidad",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: {
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        shared: true,
        formatter: function () {
          const idx = this.points?.[0]?.point?.index ?? 0;
          const row = data[idx];
          if (!row) return false;

          return `
            <b>${row.agencia}</b><br/>
            Total: <b>${row.total}</b><br/>
            <span style="color:${colorSet.turquesa}">\u25CF</span> Autorizadas: <b>${row.autorizadas}</b> (${percentage(row.autorizadas, row.total)}%)<br/>
            <span style="color:${colorSet.naranja}">\u25CF</span> Formalizadas: <b>${row.formalizadas}</b> (${percentage(row.formalizadas, row.total)}%)
          `;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 4,
          borderRadiusTopRight: 4,
        },
      },
      series: [
        {
          name: "Autorizadas",
          color: colorSet.turquesa,
          data: data.map((item) => item.autorizadas),
        },
        {
          name: "Formalizadas",
          color: colorSet.naranja,
          data: data.map((item) => item.formalizadas),
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const StatCard = ({
    value,
    label,
    accent = "#d95a6f",
    icon = "fa-solid fa-chart-line",
    detail = "",
  }) => {
    const isRangeDetail =
      typeof detail === "string" &&
      detail.trim().toLowerCase().startsWith("rango:");

    return (
      <div className="col-12 col-sm-6 col-lg-3">
        <div
          className="h-100 position-relative overflow-hidden"
          style={{
            background: isDarkMode ? "#111827" : "#ffffff",
            borderRadius: "22px",
            border: isDarkMode ? "1px solid #2a3441" : "1px solid #ececec",
            boxShadow: isDarkMode
              ? "0 10px 24px rgba(0,0,0,0.28)"
              : "0 10px 24px rgba(15, 23, 42, 0.08)",
            padding: "20px 22px 18px",
            minHeight: "138px",
            transition: "all .25s ease",
            cursor: "default",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "5px",
              background: accent,
            }}
          />

          <div className="d-flex align-items-start justify-content-between">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: "2.7rem",
                  fontWeight: 800,
                  color: textColor,
                  lineHeight: 1,
                  marginBottom: "10px",
                }}
              >
                {value}
              </div>

              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: mutedColor,
                  lineHeight: 1.3,
                  marginBottom: detail ? "8px" : 0,
                }}
              >
                {label}
              </div>

              {detail ? (
                isRangeDetail ? (
                  <div
                    style={{
                      marginTop: "2px",
                      background: isDarkMode
                        ? "rgba(47,128,237,0.16)"
                        : "rgba(47,128,237,0.08)",
                      borderLeft: `3px solid ${accent}`,
                      padding: "6px 10px",
                      borderRadius: "6px",
                      display: "inline-block",
                      fontSize: "13px",
                      lineHeight: 1.4,
                      whiteSpace: "pre-line",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        color: isDarkMode ? "#dbeafe" : "#1f3c88",
                      }}
                    >
                      Rango:
                    </span>{" "}
                    <span
                      style={{
                        fontWeight: 700,
                        color: isDarkMode ? "#ffffff" : "#111827",
                      }}
                    >
                      {detail.replace(/^Rango:\s*/i, "")}
                    </span>
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: softTextColor,
                      lineHeight: 1.35,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {detail}
                  </div>
                )
              ) : null}
            </div>

            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "14px",
                background: isDarkMode ? "rgba(255,255,255,0.06)" : "#f4f6f8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: accent,
                flexShrink: 0,
                marginLeft: "10px",
              }}
            >
              <i className={icon} style={{ fontSize: "20px" }}></i>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const cardStyle = {
    boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
    background: cardBg,
    borderRadius: "18px",
  };

  return (
    <div>
      {loading ? (
        <div className="loader-data">
          <Circles height="80" width="80" color="#4fa94d" />
        </div>
      ) : (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
            <div>
              <h1 className="page-header m-0">CONTROL DE SOLICITUDES AL DÍA</h1>
              <div
                style={{
                  color: mutedColor,
                  fontSize: "13px",
                  marginTop: "4px",
                }}
              >
                Vista general de solicitudes, conversión y desempeño
              </div>
            </div>

            <div
              style={{
                background: isDarkMode ? "#1f2937" : "#fff",
                borderRadius: "12px",
                padding: "12px 20px",
                border: isDarkMode ? "1px solid #374151" : "1px solid #d9d9d9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "240px",
                height: "90px",
              }}
            >
              <img
                src="/assets/img/gocar_negro.png"
                alt="GOCAR"
                style={{
                  maxWidth: "190px",
                  maxHeight: "62px",
                  objectFit: "contain",
                }}
              />
            </div>
          </div>

          <div className="row g-3 mb-4">
            <StatCard
              value={formatNumber(metrics.total)}
              label="Total clientes babilonia"
              detail={`Rango: ${selectedDateRangeText}`}
              accent={colorSet.azul}
              icon="fa-solid fa-file-lines"
            />

            <StatCard
              value={formatNumber(metrics.conStatus)}
              label="Solicitudes con status"
              detail={`Faltan por asignar: ${formatNumber(metrics.pendientesStatus)}\n${metrics.pctSolicitudesTotales}% del total clientes`}
              accent={colorSet.turquesa}
              icon="fa-solid fa-circle-check"
            />

            {shouldShowCreditOnlyBlocks && (
              <>
                <StatCard
                  value={`${metrics.pctAprobacion}%`}
                  label="% De Aprobación"
                  detail={`Autorizadas: ${formatNumber(metrics.autorizadas)} de ${formatNumber(metrics.totalCredito)}`}
                  accent={colorSet.turquesa}
                  icon="fa-solid fa-circle-check"
                />

                <StatCard
                  value={`${metrics.pctRechazo}%`}
                  label="% Rechazadas"
                  detail={`Rechazadas: ${formatNumber(metrics.rechazadas)} de ${formatNumber(metrics.totalCredito)}`}
                  accent={colorSet.rojo}
                  icon="fa-solid fa-circle-xmark"
                />

                <StatCard
                  value={`${metrics.pctFormalizacion}%`}
                  label="% De Formalización"
                  detail={`Formalizadas: ${formatNumber(metrics.formalizadas)} de ${formatNumber(metrics.conStatus)}`}
                  accent={colorSet.naranja}
                  icon="fa-solid fa-handshake"
                />
              </>
            )}
          </div>

          <div className="row g-3 mb-3 align-items-end">
            <div className="col-12 col-md-4 col-lg-3">
              <label className="form-label fw-semibold black-text">Fecha</label>

              <div className="d-flex align-items-stretch gap-2">
                <div className="flex-grow-1">
                  <DateRangePicker
                    key={datePickerResetKey}
                    ref={datePickerRef}
                    startDate={
                      filters.startDate
                        ? Moment(filters.startDate, "YYYY-MM-DD")
                        : Moment().startOf("month")
                    }
                    endDate={
                      filters.endDate
                        ? Moment(filters.endDate, "YYYY-MM-DD")
                        : Moment()
                    }
                    onApply={handleDateApplyEvent}
                    onCancel={clearDateFilter}
                  >
                    <button
                      type="button"
                      className="form-control text-start"
                      style={{ width: "100%" }}
                    >
                      {filters.startDate && filters.endDate
                        ? `${filters.startDate} | ${filters.endDate}`
                        : "Selecciona un rango"}
                    </button>
                  </DateRangePicker>
                </div>

                {filters.startDate || filters.endDate ? (
                  <button
                    type="button"
                    className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                    onClick={clearDateFilter}
                    title="Limpiar fecha"
                    style={{
                      minWidth: "44px",
                      width: "44px",
                      padding: "0",
                      flexShrink: 0,
                    }}
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                ) : null}
              </div>
            </div>

            <div className="col-12 col-md-4 col-lg-2">
              <label className="form-label fw-semibold black-text">
                Agencia
              </label>
              <select
                className="form-select"
                value={filters.agencia}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    agencia: e.target.value,
                  }))
                }
              >
                {selectOptions.agencia.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4 col-lg-2">
              <label className="form-label fw-semibold black-text">
                Dirección
              </label>
              <select
                className="form-select"
                value={filters.direccion}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    direccion: e.target.value,
                  }))
                }
              >
                {selectOptions.direccion.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label fw-semibold black-text">
                Departamento
              </label>
              <select
                className="form-select"
                value={filters.departamento}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    departamento: e.target.value,
                  }))
                }
              >
                {selectOptions.departamento.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label fw-semibold black-text">
                Razón Social
              </label>
              <select
                className="form-select"
                value={filters.razonSocial}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    razonSocial: e.target.value,
                  }))
                }
              >
                {selectOptions.razonSocial.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label fw-semibold black-text">
                Tipo de pago
              </label>
              <select
                className="form-select"
                value={filters.tipoPago}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    tipoPago: e.target.value,
                  }))
                }
              >
                <option value="Todas">Todas</option>
                <option value="Crédito">Crédito</option>
                <option value="Contado">Contado</option>
              </select>
            </div>

            <div className="col-12 col-lg-1 d-grid">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={clearAllFilters}
                title="Limpiar todos los filtros"
              >
                Limpiar
              </button>
            </div>
          </div>

          <div className="row g-3">
            {shouldShowStatusChart && (
              <div className="col-12 col-xl-6">
                <Card style={cardStyle} className="h-100">
                  <CardHeader>Status y substatus de crédito</CardHeader>
                  <CardBody>
                    <Chart
                      key={`status-substatus-${chartRenderKey}`}
                      options={statusSubstatusChartOptions}
                      immutable={true}
                      containerProps={{
                        style: { width: "100%", height: "420px" },
                      }}
                    />
                  </CardBody>
                </Card>
              </div>
            )}

            {shouldShowContadoStatusChart && (
              <div className="col-12 col-xl-6">
                <Card style={cardStyle} className="h-100">
                  <CardHeader>Status de contado</CardHeader>
                  <CardBody>
                    <Chart
                      key={`contado-status-${chartRenderKey}`}
                      options={contadoStatusChartOptions}
                      immutable={true}
                      containerProps={{
                        style: { width: "100%", height: "340px" },
                      }}
                    />
                  </CardBody>
                </Card>
              </div>
            )}

            <div className="col-12 col-md-6 col-xl-3">
              <Card style={cardStyle} className="h-100">
                <CardHeader>Total Solicitudes por Fuente</CardHeader>
                <CardBody>
                  <Chart
                    key={`fuente-${chartRenderKey}`}
                    options={fuenteChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "380px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-md-6 col-xl-3">
              <Card style={cardStyle} className="h-100">
                <CardHeader>Total Solicitudes por Financiera</CardHeader>
                <CardBody>
                  <Chart
                    key={`financiera-${chartRenderKey}`}
                    options={financieraChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "380px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-lg-6">
              <Card style={cardStyle} className="h-100">
                <CardHeader>Comparativo Tipo de Pago</CardHeader>
                <CardBody>
                  <Chart
                    key={`tipo-pago-${chartRenderKey}`}
                    options={tipoPagoComparacionChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "360px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-lg-6">
              <Card style={cardStyle} className="h-100">
                <CardHeader>Prueba de manejo</CardHeader>
                <CardBody>
                  <Chart
                    key={`prueba-manejo-${chartRenderKey}`}
                    options={pruebaManejoChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "360px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-lg-6">
              <Card style={cardStyle} className="h-100">
                <CardHeader>Planes más seleccionados</CardHeader>
                <CardBody>
                  <Chart
                    key={`planes-seleccionados-${chartRenderKey}`}
                    options={planesSeleccionadosChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "360px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-lg-6">
              <Card style={cardStyle} className="h-100">
                <CardHeader>
                  Top 5 Accesorios / Garantías más seleccionados
                </CardHeader>
                <CardBody>
                  <Chart
                    key={`top-accesorios-garantias-${chartRenderKey}`}
                    options={topAccesoriosGarantiasChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "380px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12">
              <Card style={cardStyle} className="h-100">
                <CardHeader>
                  Comparativo de ganancias de accesorios y garantías
                </CardHeader>
                <CardBody>
                  <Chart
                    key={`ganancias-accesorios-garantias-${chartRenderKey}`}
                    options={gananciasAccesoriosGarantiasChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "390px" },
                    }}
                  />

                  <div className="table-responsive mt-3">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th className="black-text">Concepto</th>
                          <th className="black-text">
                            {gananciasAccesoriosGarantiasResumen.previousLabel}
                          </th>
                          <th className="black-text">
                            {gananciasAccesoriosGarantiasResumen.currentLabel}
                          </th>
                          <th className="black-text">Diferencia</th>
                          <th className="black-text">Productos vendidos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gananciasAccesoriosGarantiasRows.map((row) => (
                          <tr key={`ganancia-row-${row.key}`}>
                            <td>{row.label}</td>
                            <td>{formatCurrency(row.previous)}</td>
                            <td>{formatCurrency(row.current)}</td>
                            <td>
                              {formatCurrency(row.diff)} ({row.diffPct}%)
                            </td>
                            <td>
                              {formatNumber(row.currentCount)} actual /{" "}
                              {formatNumber(row.previousCount)} anterior
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-xl-6">
              <Card style={cardStyle} className="h-100">
                <CardHeader>
                  Total Solicitudes por Agencia y Departamento
                </CardHeader>
                <CardBody>
                  <Chart
                    key={`agencia-departamento-${chartRenderKey}`}
                    options={agenciaDepartamentoChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "380px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div
              className={
                shouldShowCreditOnlyBlocks ? "col-12 col-xl-6" : "col-12"
              }
            >
              <Card style={cardStyle} className="h-100">
                <CardHeader>
                  Acumulado Mes Actual y Acumulado Mes Anterior por Día
                </CardHeader>
                <CardBody>
                  <Chart
                    key={`acumulado-${chartRenderKey}`}
                    options={acumuladoChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "360px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            {shouldShowCreditOnlyBlocks && (
              <>
                <div className="col-12 col-lg-4">
                  <Card style={cardStyle} className="h-100">
                    <CardHeader>Total Solicitudes por Estatus</CardHeader>
                    <CardBody>
                      <Chart
                        key={`status-simple-${chartRenderKey}`}
                        options={statusSimpleChartOptions}
                        immutable={true}
                        containerProps={{
                          style: { width: "100%", height: "340px" },
                        }}
                      />
                    </CardBody>
                  </Card>
                </div>

                <div className="col-12 col-lg-4">
                  <Card style={cardStyle} className="h-100">
                    <CardHeader>Subestatus General</CardHeader>
                    <CardBody>
                      <Chart
                        key={`substatus-simple-${chartRenderKey}`}
                        options={substatusSimpleChartOptions}
                        immutable={true}
                        containerProps={{
                          style: { width: "100%", height: "380px" },
                        }}
                      />
                    </CardBody>
                  </Card>
                </div>
              </>
            )}

            <div
              className={
                shouldShowCreditOnlyBlocks ? "col-12 col-lg-4" : "col-12"
              }
            >
              <Card style={cardStyle} className="h-100">
                <CardHeader>Embudo de Conversión</CardHeader>
                <CardBody>
                  <Chart
                    key={`funnel-${chartRenderKey}`}
                    highcharts={Highcharts}
                    options={funnelChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "380px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12">
              <Card style={cardStyle} className="h-100">
                <CardHeader>Tendencia de Solicitudes por Día</CardHeader>
                <CardBody>
                  <Chart
                    key={`tendencia-${chartRenderKey}`}
                    options={tendenciaDiariaChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "340px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-lg-6">
              <Card style={cardStyle} className="h-100">
                <CardHeader>Top 10 Agencias por Solicitudes</CardHeader>
                <CardBody>
                  <Chart
                    key={`top-agencias-${chartRenderKey}`}
                    options={topAgenciasChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "400px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            {shouldShowCreditOnlyBlocks && (
              <>
                <div className="col-12 col-lg-6">
                  <Card style={cardStyle} className="h-100">
                    <CardHeader>Top 10 Agencias por Formalización</CardHeader>
                    <CardBody>
                      <Chart
                        key={`top-formalizacion-${chartRenderKey}`}
                        options={topAgenciasFormalizacionChartOptions}
                        immutable={true}
                        containerProps={{
                          style: { width: "100%", height: "400px" },
                        }}
                      />
                    </CardBody>
                  </Card>
                </div>

                <div className="col-12">
                  <Card style={cardStyle} className="h-100">
                    <CardHeader>
                      Autorización vs Formalización por Agencia
                    </CardHeader>
                    <CardBody>
                      <Chart
                        key={`conversion-agencia-${chartRenderKey}`}
                        options={conversionAgenciaChartOptions}
                        immutable={true}
                        containerProps={{
                          style: { width: "100%", height: "400px" },
                        }}
                      />
                    </CardBody>
                  </Card>
                </div>

                <div className="col-12 col-xl-8">
                  <Card style={cardStyle} className="h-100">
                    <CardHeader>Asesor</CardHeader>
                    <CardBody style={{ overflowX: "auto" }}>
                      <table className="table table-sm align-middle mb-0">
                        <thead style={{ position: "sticky", top: 0 }}>
                          <tr>
                            <th className="black-text">#</th>
                            <th className="black-text">Asesor</th>
                            <th className="black-text">Total Solicitudes</th>
                            <th className="black-text">Crédito</th>
                            <th className="black-text">Contado</th>
                            <th className="black-text">Autorizadas</th>
                            <th className="black-text">% Autorizadas</th>
                            <th className="black-text">Rechazadas</th>
                            <th className="black-text">% Rechazadas</th>
                            <th className="black-text">Formalizadas</th>
                            <th className="black-text">Pruebas manejo</th>
                            <th className="black-text">Pendientes Buró</th>
                            <th className="black-text">% Participación</th>
                          </tr>
                        </thead>
                        <tbody>
                          {asesorRows.length > 0 ? (
                            asesorRows.map((row, index) => (
                              <tr key={row.asesor}>
                                <td>{index + 1}</td>
                                <td>{row.asesor}</td>
                                <td>{row.total}</td>
                                <td>{row.creditos}</td>
                                <td>{row.contados}</td>
                                <td>{row.autorizadas}</td>
                                <td>
                                  {row.pctAutorizadas}% ({row.autorizadas} de{" "}
                                  {row.creditos})
                                </td>
                                <td>{row.rechazadas}</td>
                                <td>
                                  {row.pctRechazadas}% ({row.rechazadas} de{" "}
                                  {row.creditos})
                                </td>
                                <td>{row.formalizadas}</td>
                                <td>{row.pruebasManejo}</td>
                                <td>{row.pendientesBuro}</td>
                                <td>
                                  {row.pctParticipacion}% ({row.total})
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="13" className="text-center">
                                Sin datos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </CardBody>
                  </Card>
                </div>

                <div className="col-12 col-xl-4">
                  <div className="row g-3">
                    <div className="col-12">
                      <Card style={cardStyle} className="h-100">
                        <CardHeader>
                          Total Formalizaciones por Financiera
                        </CardHeader>
                        <CardBody>
                          <Chart
                            key={`formalizaciones-financiera-${chartRenderKey}`}
                            options={formalizacionesFinancieraChartOptions}
                            immutable={true}
                            containerProps={{
                              style: { width: "100%", height: "320px" },
                            }}
                          />
                        </CardBody>
                      </Card>
                    </div>

                    <div className="col-12">
                      <Card style={cardStyle} className="h-100">
                        <CardHeader>Dirección</CardHeader>
                        <CardBody style={{ overflowX: "auto" }}>
                          <table className="table table-sm align-middle mb-0">
                            <thead>
                              <tr>
                                <th className="black-text">Dirección</th>
                                <th className="black-text">Total</th>
                                <th className="black-text">Formalizaciones</th>
                                <th className="black-text">% De Aprobación</th>
                              </tr>
                            </thead>
                            <tbody>
                              {direccionRows.length > 0 ? (
                                direccionRows.map((row) => (
                                  <tr key={row.direccion}>
                                    <td>{row.direccion}</td>
                                    <td>{row.total}</td>
                                    <td>{row.formalizaciones}</td>
                                    <td>
                                      {row.pctAprobacion}% ({row.autorizadas} de{" "}
                                      {row.total})
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center">
                                    Sin datos
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </CardBody>
                      </Card>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <Card style={cardStyle} className="h-100">
                    <CardHeader>
                      Ranking de asesores por paquetes vendidos
                    </CardHeader>
                    <CardBody style={{ overflowX: "auto" }}>
                      <table className="table table-sm align-middle mb-0">
                        <thead style={{ position: "sticky", top: 0 }}>
                          <tr>
                            <th className="black-text">#</th>
                            <th className="black-text">Asesor</th>
                            <th className="black-text">Elite</th>
                            <th className="black-text">Gold</th>
                            <th className="black-text">Platinum</th>
                            <th className="black-text">Plan cliente</th>
                            <th className="black-text">
                              Total paquetes vendidos
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {asesorPaquetesRows.length > 0 ? (
                            asesorPaquetesRows.map((row, index) => (
                              <tr key={`asesor-paquetes-${row.asesor}`}>
                                <td>{index + 1}</td>
                                <td>{row.asesor}</td>
                                <td>{row.Elite || 0}</td>
                                <td>{row.Gold || 0}</td>
                                <td>{row.Platinum || 0}</td>
                                <td>{row["Plan cliente"] || 0}</td>
                                <td>{row.totalPaquetesVendidos}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center">
                                Sin datos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </CardBody>
                  </Card>
                </div>

                <div className="col-12">
                  <Card style={cardStyle} className="h-100">
                    <CardHeader>
                      Ranking de asesores por accesorios y garantías vendidos
                    </CardHeader>
                    <CardBody style={{ overflowX: "auto" }}>
                      <table className="table table-sm align-middle mb-0">
                        <thead style={{ position: "sticky", top: 0 }}>
                          <tr>
                            <th className="black-text">#</th>
                            <th className="black-text">Asesor</th>

                            {accesoriosGarantiasColumns.map((col) => (
                              <th
                                key={`col-${col.key}`}
                                className="black-text"
                                title={`${col.title} (${col.type})`}
                              >
                                {truncateText(col.title, 24)}
                              </th>
                            ))}

                            <th className="black-text">Total items vendidos</th>
                          </tr>
                        </thead>

                        <tbody>
                          {asesorAccesoriosGarantiasMatrixRows.length > 0 ? (
                            asesorAccesoriosGarantiasMatrixRows.map(
                              (row, index) => (
                                <tr key={`asesor-matrix-${row.asesor}`}>
                                  <td>{index + 1}</td>
                                  <td>{row.asesor}</td>

                                  {accesoriosGarantiasColumns.map((col) => (
                                    <td key={`cell-${row.asesor}-${col.key}`}>
                                      {row[col.key] || 0}
                                    </td>
                                  ))}

                                  <td>{row.totalItemsVendidos}</td>
                                </tr>
                              ),
                            )
                          ) : (
                            <tr>
                              <td
                                colSpan={accesoriosGarantiasColumns.length + 3}
                                className="text-center"
                              >
                                Sin datos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </CardBody>
                  </Card>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default DashboardRendimiento;
